import {
    Box, Text,
    Flex, Input,
    Button,
    InputGroup, InputLeftElement,
    SimpleGrid, Divider
} from "@chakra-ui/react";
import { React, useEffect, useState, useRef } from 'react';
import back from "../../../../assets/images/back.png";
import { useNavigate } from "react-router-dom";
import EventsService from '../../../../services/events/eventsService';
import { BASE_URL } from "../../../../config";
import moment from 'moment';
import Event from "../../components/EventCard/EventCard";
import { useTranslation } from "react-i18next";
import DatePicker, { registerLocale } from "react-datepicker";
import en from "date-fns/locale/en-GB";
import pl from "date-fns/locale/pl";
import "react-datepicker/dist/react-datepicker.css";
import { forwardRef } from "react";
import { Trans } from 'react-i18next';
import useFavorites from './useFavorites';
import { useAppToast } from "../../../../hooks/useAppToast";

registerLocale("en", en);
registerLocale("pl", pl);

const AllEventsPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation("default");
    const user = JSON.parse(localStorage.getItem("user"));
    const isSuperAdmin = user?.roles?.includes('SuperAdmin') ?? false;
    const [events, setEvents] = useState([]);
    const { favorites, toggleFavorite } = useFavorites(events, setEvents);
    const [loading, setLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState("");
    const [location, setLocation] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [sort, setSort] = useState('createdAt_desc');
    const [currentPage, setCurrentPage] = useState(1);

    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;

    const toast = useAppToast();
    const baseURL = BASE_URL;

    const [availableHashtags, setAvailableHashtags] = useState([]);

    const CustomInput = forwardRef(({ value, onClick }, ref) => (
        <InputGroup flex="1">
            <InputLeftElement>
                <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5.16667 4.33333V1M11.8333 4.33333V1M4.33333 7.66667H12.6667M2.66667 16H14.3333C14.7754 16 15.1993 15.8244 15.5118 15.5118C15.8244 15.1993 16 14.7754 16 14.3333V4.33333C16 3.89131 15.8244 3.46738 15.5118 3.15482C15.1993 2.84226 14.7754 2.66667 14.3333 2.66667H2.66667C2.22464 2.66667 1.80072 2.84226 1.48816 3.15482C1.17559 3.46738 1 3.89131 1 4.33333V14.3333C1 14.7754 1.17559 15.1993 1.48816 15.5118C1.80072 15.8244 2.22464 16 2.66667 16Z" stroke="#6E6064" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </InputLeftElement>
            <Input
                border={'0px'}
                borderRadius={'12px'}
                ref={ref}
                value={value}
                onClick={onClick}
                placeholder={t('placeholder.date_range')}
                readOnly
            />
        </InputGroup>
    ));

    const fetchEvents = async () => {
        setLoading(true);
        try {
            const res = await EventsService.getAllEvents({
                page: currentPage,
                search,
                dateFrom,
                dateTo,
                sort,
                location
            });
            setEvents(res.data.data.items);
            setTotalPages(res.data.data.totalPages);

            const hashtagsResponse = await EventsService.getHashtags();
            setAvailableHashtags(hashtagsResponse.data.data.map(h => ({ value: h.name, label: h.name })));
        } catch (error) {
            toast({ title: t('request.error'), status: 'error', duration: 3000, isClosable: true });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEvents();
        const interval = setInterval(() => {
            fetchEvents();
        }, 10000);

        return () => clearInterval(interval);
    }, [sort, search, dateFrom, dateTo, currentPage, location]);

    useEffect(() => {
        if (startDate) {
            setDateFrom(moment(startDate).format("YYYY-MM-DD"));
        } else {
            setDateFrom("");
        }

        if (endDate) {
            setDateTo(moment(endDate).format("YYYY-MM-DD"));
        } else {
            setDateTo("");
        }
    }, [startDate, endDate]);

    return (
        <Flex flexDirection={'column'} w={"100%"}>

            <Flex alignItems={"center"} justifyContent="center" w={"100%"} flexDirection={'column'} bg={'#3A0F1B'} px={{ base: 1, md: 0 }}>
                <Flex flexDirection={'column'} w={{ base: '100%', sm: '7xl' }} alignItems={'flex-start'} py={{ base: '32px', md: '80px' }}>

                    <Text fontFamily="body" fontWeight={'500'} fontSize={'24px'} color='#FFFFFF'>
                        {t('event.filter')}
                    </Text>
                    {/* Form Filter */}
                    <Flex
                        flexDirection={"column"}
                        mb={{ base: '16px', md: 8 }}
                        py={{ base: '10px', md: '32px' }}
                        rounded="12px"
                        gap={'16px'}
                        mx={{ base: '16px', md: 0 }}
                        w="100%"
                    >
                        <Flex gap={{ base: '4px', md: '16px' }} direction={{ base: 'column', md: 'row' }} w="100%">
                            <Flex borderRadius={{ base: '20px', md: '100px' }} bg={"white"} alignItems={'center'} fontFamily="body" fontWeight={'400'}
                                fontSize={'18px'} color={'#6E6064'} alignSelf='center'
                                justify="center" w={{ base: '70%', md: '100%' }} flexDirection={{ base: 'column', md: 'row' }} px={{ base: 2, md: 0 }}>
                                <InputGroup borderRight={{ base: 'none', md: '1px solid #6E6064' }} flex="1">
                                    <InputLeftElement>
                                        <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M12.0869 12.0869L16 16" stroke="#6E6064" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M7.19565 13.3913C10.6174 13.3913 13.3913 10.6174 13.3913 7.19565C13.3913 3.77389 10.6174 1 7.19565 1C3.77389 1 1 3.77389 1 7.19565C1 10.6174 3.77389 13.3913 7.19565 13.3913Z" stroke="#6E6064" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </InputLeftElement>
                                    <Input border={'0px'} borderRadius={'12px'}
                                        placeholder={t('placeholder.search')}
                                        value={search} _focusVisible={{ shadow: 'outline' }} _focus={{ shadow: 'none' }}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </InputGroup>

                                <Box flex="1" borderRight={{ base: 'none', md: '1px solid #6E6064' }} alignSelf={{ base: 'baseline', md: 'center' }}>
                                    <DatePicker
                                        selectsRange={true}
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={(update) => setDateRange(update)}
                                        isClearable={true}
                                        customInput={<CustomInput />}
                                        locale="pl"
                                        showMonthDropdown
                                        showYearDropdown
                                        dateFormat="dd.MM.yyyy"
                                    />
                                </Box>

                                <InputGroup flex="1">
                                    <InputLeftElement>
                                        <svg width="15" height="17" viewBox="0 0 15 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M12.0819 12.0816L8.63892 15.5246C8.48835 15.6753 8.30955 15.7949 8.11275 15.8764C7.91594 15.958 7.70499 16 7.49195 16C7.2789 16 7.06795 15.958 6.87114 15.8764C6.67434 15.7949 6.49554 15.6753 6.34497 15.5246L2.90121 12.0816C1.99339 11.1738 1.37516 10.0171 1.12471 8.75785C0.874261 7.49862 1.00283 6.19341 1.49417 5.00726C1.98551 3.8211 2.81755 2.80728 3.88507 2.094C4.95259 1.38071 6.20765 1 7.49154 1C8.77543 1 10.0305 1.38071 11.098 2.094C12.1655 2.80728 12.9976 3.8211 13.4889 5.00726C13.9802 6.19341 14.1088 7.49862 13.8584 8.75785C13.6079 10.0171 12.9897 11.1738 12.0819 12.0816Z" stroke="#6E6064" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M9.92584 7.4913C9.92584 8.13693 9.66937 8.75611 9.21285 9.21263C8.75632 9.66916 8.13714 9.92563 7.49152 9.92563C6.84589 9.92563 6.22671 9.66916 5.77019 9.21263C5.31366 8.75611 5.05719 8.13693 5.05719 7.4913C5.05719 6.84568 5.31366 6.2265 5.77019 5.76997C6.22671 5.31345 6.84589 5.05698 7.49152 5.05698C8.13714 5.05698 8.75632 5.31345 9.21285 5.76997C9.66937 6.2265 9.92584 6.84568 9.92584 7.4913Z" stroke="#6E6064" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </InputLeftElement>
                                    <Input border={'0px'} borderRadius={'12px'}
                                        placeholder={t('placeholder.location')}
                                        value={location} _focusVisible={{ shadow: 'outline' }} _focus={{ shadow: 'none' }}
                                        onChange={(e) => setLocation(e.target.value)}
                                    />
                                </InputGroup>
                            </Flex>
                            <Box display={{ base: "none", md: "inline" }}>
                                <Button bg={'#7A1F35'} w={'158px'} h={'59px'} color={"white"} gap={'10px'}
                                    fontFamily="body" fontWeight={'600'} fontSize={'16px'}
                                    onClick={() => setCurrentPage(1)} borderRadius={'100px'}>
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" mt={'3px'} ml={'4px'}>
                                        <path d="M10 9.75L13 12.75" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M6.25 10.75C8.87335 10.75 11 8.62335 11 6C11 3.37665 8.87335 1.25 6.25 1.25C3.62665 1.25 1.5 3.37665 1.5 6C1.5 8.62335 3.62665 10.75 6.25 10.75Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    {t('search')}
                                </Button>
                            </Box>
                        </Flex>
                    </Flex>

                </Flex>
            </Flex>

            <Flex alignItems={"center"} justifyContent="center" w={"100%"} flexDirection={'column'} bg={'#FFFFFF'} px={{ base: 1, md: 0 }}>
                <Flex flexDirection={'column'} w={{ base: '100%', sm: '7xl' }} alignItems={'flex-start'} py={{ base: '32px', md: '80px' }}>

                    <Text fontFamily="body" fontWeight={'700'} fontSize={'24px'} color='#1E1517'>
                        {t('check.news')}
                    </Text>
                    <Divider borderColor={'#7A1F35'} mt={'4px'} mb={'33px'} h={'2px'} />
                    {/* Events List */}
                    <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={'32px'} px={{ base: '16px', md: 0 }}>
                        {!loading && events.length === 0 ? (
                            <Text fontFamily="body" fontWeight={'500'} fontSize={'20px'} color="#1E1517">
                                {t('event.not_found')}
                            </Text>
                        ) : (
                            events.slice(0, 4).map((event) => (
                                <Event event={event} onFavoriteToggle={toggleFavorite} isSuperAdmin={isSuperAdmin} />
                            ))
                        )}
                    </SimpleGrid>
                </Flex>
            </Flex>

            {favorites?.length > 0 && <Flex alignItems={"center"} justifyContent="center" w={"100%"} flexDirection={'column'}
                bgColor={'#3A0F1B'} bgImg={back} backgroundSize="cover" backgroundPosition="center" backgroundRepeat="no-repeat" px={{ base: 1, md: 0 }}>
                <Flex flexDirection={'column'} w={{ base: '100%', sm: '7xl' }} alignItems={'flex-start'} py={{ base: '32px', md: '80px' }}>

                    <Flex alignItems={'center'} gap={'8px'}>
                        <Text fontFamily="body" fontWeight={'700'} fontSize={'24px'} color={'#FFFFFF'}>
                            {t('event.favorite')}
                        </Text>
                        <svg width="20" height="17" viewBox="0 0 20 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3.17159 2.70102C2.80015 3.08179 2.50551 3.53382 2.30449 4.03132C2.10346 4.52882 2 5.06203 2 5.60052C2 6.13901 2.10346 6.67222 2.30449 7.16972C2.50551 7.66722 2.80015 8.11925 3.17159 8.50002L10 15.5L16.8284 8.50002C17.5786 7.73102 18 6.68804 18 5.60052C18 4.513 17.5786 3.47002 16.8284 2.70102C16.0783 1.93203 15.0609 1.50001 14 1.50001C12.9391 1.50001 11.9217 1.93203 11.1716 2.70102L10 3.90201L8.82845 2.70102C8.45702 2.32025 8.01606 2.01821 7.53076 1.81214C7.04546 1.60606 6.52531 1.5 6.00002 1.5C5.47473 1.5 4.95458 1.60606 4.46928 1.81214C3.98398 2.01821 3.54302 2.32025 3.17159 2.70102Z" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </Flex>
                    <Divider borderColor={'#7A1F35'} mt={'4px'} mb={'33px'} h={'2px'} />
                    {/* Events List */}
                    <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={'32px'} px={{ base: '16px', md: 0 }}>
                        {!loading && events.length === 0 ? (
                            <Text fontFamily="body" fontWeight={'500'} fontSize={'20px'} color="#FFFFFF">
                                {t('event.not_found')}
                            </Text>
                        ) : (
                            favorites && favorites.map((event) => (
                                <Event event={event} color={'#B0B0B0'} titleColor={'#FFFFFF'} onFavoriteToggle={toggleFavorite} isSuperAdmin={isSuperAdmin} />
                            ))
                        )}
                    </SimpleGrid>

                </Flex>
            </Flex>}

            <Flex alignItems={"center"} justifyContent="center" w={"100%"} flexDirection={'column'}>

                <Flex flexDirection={'column'} gap={{ base: '16px', md: '48px' }} w={{ base: '100%', sm: '7xl' }} alignItems={'flex-start'} py={{ base: '32px', md: '80px' }}
                    bgColor={"F3F3F3"} id="events" px={{ base: 1, md: 0 }}>
                    <Box w={'full'}>

                        <Text fontFamily="body" fontWeight={'700'} fontSize={'24px'} color='#1E1517'>
                            {t('event.popular')}
                        </Text>
                        <Divider borderColor={'#7A1F35'} mt={'4px'} mb={'33px'} h={'2px'} />

                        {/* Events List */}
                        <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={'32px'} px={{ base: '16px', md: 0 }} bg={'#F8F5F4'}>
                            {!loading && events.length === 0 ? (
                                <Text fontFamily="body" fontWeight={'500'} fontSize={'20px'} color="#1E1517">
                                    {t('event.not_found')}
                                </Text>
                            ) : (
                                events.map((event) => (
                                    <Event event={event} onFavoriteToggle={toggleFavorite} isSuperAdmin={isSuperAdmin} />
                                ))
                            )}
                        </SimpleGrid>

                        <Text fontFamily="body" fontWeight={'700'} fontSize={'24px'} color='#1E1517'>
                            <Trans i18nKey="event.hashtags" components={{ b: <b /> }} />
                        </Text>
                        <Divider borderColor={'#7A1F35'} mt={'4px'} mb={'33px'} h={'2px'} />
                        <Flex gap={{ base: '4px', md: '16px' }}
                            flexWrap="wrap"
                            justify={{ base: 'center', md: 'flex-start' }}
                            w={{ base: '100%', md: 'auto' }}>
                            {availableHashtags.map((hashtag) => (
                                <Button bg={'#F5E4E9'} px={'8px'} py={'16px'} borderRadius={'12px'} w={{ base: 'calc(50% - 4px)', md: 'auto' }}>
                                    <Text fontFamily="body" fontWeight={'600'} fontSize='16px'>
                                        #{hashtag.label}
                                    </Text>
                                </Button>
                            ))}
                        </Flex>
                    </Box>

                </Flex>
            </Flex>
        </Flex>
    )
}

export default AllEventsPage;
